/**
 * The data boundary. One SolumClient — a live PostgREST transport when
 * VITE_SUPABASE_URL/TOKEN are configured, otherwise the fixture (standalone dev +
 * demo). TanStack Query provides caching + optimistic updates on top. Swapping
 * dev → a real deployment is configuration, never a code change.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { QUERY_RETRY, health } from './dependencyHealth'
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createClient, postgrestTransport, type SolumClient, type Transport } from '@solum/sdk'
import { QUERY_FOCUS_RECOVERY_DEFAULTS } from '@solum/experience'
import { authClient, useAuth } from '@solum/auth'
import { runtimeEnv } from '@solum/auth/runtime-config'
import { reportWriteFailure, shouldReportWriteFailure, writeFailureSubject } from './writeState'

const url = import.meta.env.VITE_SUPABASE_URL
// '' targets a bare PostgREST at the root (the local stack); unset ⇒ the Supabase '/rest/v1' route.
const restPath = import.meta.env.VITE_SUPABASE_REST_PATH as string | undefined

// Live-ness is the URL alone. It used to be `url && VITE_SUPABASE_TOKEN` — a JWT
// compiled into the bundle, shared by every browser, expiring in 2027 (SEC-03).
// The token now comes from the signed-in user's session.
const isLive = Boolean(url)

/**
 * The fixture as a BUILD boundary (UX-11/SIM-06, #393), without top-level await.
 *
 * Every Transport method is async, so a synchronous shell can forward each call
 * to the real fixture transport once its chunk arrives — the chunk starts
 * loading at module scope (first paint never waits on the first query), but
 * nothing awaits it during module evaluation. The first attempt here was
 * `await import('./fixture')` at top level: the dev server ran it fine and the
 * PRODUCTION build rendered a blank page with no console error, no page error
 * and no failed request — TLA in the entry graph silently never resolved under
 * the built chunk order. Playwright's fixture-mode build caught it; do not
 * reintroduce TLA in this module.
 */
function lazyFixtureTransport(): Transport {
  const t = import('./fixture').then((m) => m.fixtureTransport())
  const observed = (method: keyof Transport, target: string, args: unknown) => {
    /* Browser acceptance needs to prove the focus-recovery NET does not turn
       one stale query into a request storm. HTTP interception cannot see the
       offline fixture transport, so the e2e build records this boundary
       directly. The flag is set only by playwright.config.ts; a live build
       statically drops this whole fixture branch. */
    if (import.meta.env.VITE_SOLUM_E2E_REQUESTS !== '1' || typeof window === 'undefined') return
    const calls = Reflect.get(window, '__solumFixtureRequests')
    const log = Array.isArray(calls) ? calls : []
    log.push({ method, target, args })
    Reflect.set(window, '__solumFixtureRequests', log)
  }
  const refuseWrite = (method: 'insert' | 'update' | 'delete', target: string) => {
    /* A deterministic browser instrument for the write-refusal journey.

       Fixture mode has no session and therefore cannot honestly invent an RLS
       persona (fixture/transport.ts says so explicitly). Playwright still has
       to exercise the UI contract at the REAL transport boundary, though: a
       server refusal must leave the editor, URL and dirty values intact. The
       e2e-only build flag already exposes request observation at this boundary;
       this adjacent one-shot rule makes exactly one matching write answer the
       way PostgREST answers a governed 403. It is absent from ordinary fixture
       builds and statically absent from live builds with this whole branch.

       Tests install the rule with addInitScript, before the application loads:
       { method: 'update', target: 'core.organization', status: 403, remaining: 1 }.
       Matching both the verb and target keeps an unrelated background write
       from consuming the refusal and making the journey timing-dependent. */
    if (import.meta.env.VITE_SOLUM_E2E_REQUESTS !== '1' || typeof window === 'undefined') return
    const candidate = Reflect.get(window, '__solumFixtureWriteRefusal')
    if (!candidate || typeof candidate !== 'object') return
    const rule = candidate as Record<string, unknown>
    if (rule.method !== method || rule.target !== target) return
    const remaining = typeof rule.remaining === 'number' ? rule.remaining : 1
    if (remaining <= 0) return
    Reflect.set(window, '__solumFixtureWriteRefusal', { ...rule, remaining: remaining - 1 })
    const status = typeof rule.status === 'number' ? rule.status : 403
    throw new Error(`Request failed (${status}): governed fixture write refusal`)
  }
  const refuseRead = (target: string) => {
    if (import.meta.env.VITE_SOLUM_E2E_REQUESTS !== '1' || typeof window === 'undefined') return
    const candidate = Reflect.get(window, '__solumFixtureReadRefusal')
    if (!candidate || typeof candidate !== 'object') return
    const rule = candidate as Record<string, unknown>
    if (rule.target !== target) return
    const remaining = typeof rule.remaining === 'number' ? rule.remaining : 1
    if (remaining <= 0) return
    Reflect.set(window, '__solumFixtureReadRefusal', { ...rule, remaining: remaining - 1 })
    const status = typeof rule.status === 'number' ? rule.status : 403
    throw new Error(`Request failed (${status}): governed fixture read refusal`)
  }
  const delayWrite = async (method: 'insert' | 'update', target: string) => {
    // Deterministic save-order proof at the same fixture-only boundary as
    // refusal. No clock race and no instrumentation in a live build.
    if (import.meta.env.VITE_SOLUM_E2E_REQUESTS !== '1' || typeof window === 'undefined') return
    const candidate = Reflect.get(window, '__solumFixtureWriteDelay')
    if (!candidate || typeof candidate !== 'object') return
    const rule = candidate as Record<string, unknown>
    if (rule.method !== method || rule.target !== target || rule.remaining === 0) return
    Reflect.set(window, '__solumFixtureWriteDelay', { ...rule, remaining: 0, waiting: true })
    await new Promise<void>((resolve) => {
      Reflect.set(window, '__solumFixtureReleaseWrite', () => {
        Reflect.deleteProperty(window, '__solumFixtureReleaseWrite')
        resolve()
      })
    })
  }
  return {
    rpcScalar: async (fn, args, schema) => {
      observed('rpcScalar', `${schema ?? 'core'}.${fn}`, args)
      return (await t).rpcScalar(fn, args, schema)
    },
    rpcTable: async (fn, args, schema) => {
      observed('rpcTable', `${schema ?? 'core'}.${fn}`, args)
      return (await t).rpcTable(fn, args, schema)
    },
    select: async (schema, table, opts) => {
      observed('select', `${schema}.${table}`, opts)
      refuseRead(`${schema}.${table}`)
      return (await t).select(schema, table, opts)
    },
    selectPage: async (schema, table, opts) => {
      observed('selectPage', `${schema}.${table}`, opts)
      return (await t).selectPage(schema, table, opts)
    },
    insert: async (schema, table, row) => {
      observed('insert', `${schema}.${table}`, row)
      refuseWrite('insert', `${schema}.${table}`)
      await delayWrite('insert', `${schema}.${table}`)
      return (await t).insert(schema, table, row)
    },
    update: async (schema, table, id, patch) => {
      observed('update', `${schema}.${table}`, { id, patch })
      refuseWrite('update', `${schema}.${table}`)
      await delayWrite('update', `${schema}.${table}`)
      return (await t).update(schema, table, id, patch)
    },
    delete: async (schema, table, id) => {
      observed('delete', `${schema}.${table}`, { id })
      refuseWrite('delete', `${schema}.${table}`)
      return (await t).delete(schema, table, id)
    },
  }
}

// The condition is the RAW env expression, not `isLive`, on purpose: Vite
// replaces `import.meta.env.VITE_SUPABASE_URL` with a literal at build time, so
// in a LIVE build the `else` branch is statically dead and Rollup drops
// `lazyFixtureTransport` and its dynamic import with it — the entire fixture
// dataset (~212 kB of demo rows) never ships to a real deployment. Verified by
// building with the env set: zero `fixtureTransport` references in dist. A
// standalone/e2e build (no url configured) keeps the fixture as its own lazy
// chunk instead, and statically drops the live transport + auth.
const client: SolumClient = import.meta.env.VITE_SUPABASE_URL
  // A GETTER, not a value: the access token is short-lived and rotates
  // underneath a long-lived client. Resolving per request means a refresh
  // mid-session is invisible here, and an expired token is never sent twice.
  ? createClient(postgrestTransport({
      url: url!,
      token: () => authClient.getAccessToken(),
      anonKey: runtimeEnv('VITE_SUPABASE_ANON_KEY'),
      restPath,
      /* Every request this deployment's data plane answers, or fails to (#1790).
         Observed HERE and not at the query cache: a queryFn written
         `try { … } catch { return null }` — there are six in this repo, three of
         them in `queries.ts` — resolves after swallowing a 503, so the cache
         reports a success that never happened and the degraded region dismisses
         itself mid-outage. The transport sees the wire. */
      onOutcome: (outcome) => { health.observe('data', outcome) },
    }))
  : createClient(lazyFixtureTransport())

/**
 * How this host reaches private BYTES (#1744, ruled 54560).
 *
 * Exported from here rather than assembled at a call site so there is exactly
 * one place that knows this deployment's base URL, its anon key and where the
 * caller's token comes from — the same reason the PostgREST client is built
 * here. `fetchAuthenticatedObject` owns the wire; this owns the configuration.
 *
 * A getter for the token, deliberately, matching the transport above: a token
 * captured once is a stale identity, and on a byte request identity is the
 * whole control.
 */
export const storageAccess = {
  baseUrl: url as string | undefined,
  token: () => authClient.getAccessToken(),
  anonKey: runtimeEnv('VITE_SUPABASE_ANON_KEY'),
}

const Ctx = createContext<{ client: SolumClient; isLive: boolean }>({ client, isLive })
export const useSolum = () => useContext(Ctx)

/**
 * Queries had a policy here and mutations had none (#877).
 *
 * That is the whole asymmetry: every read went through `QueryBoundary`, which
 * classifies a 403 as `unauthorized` and says so, while every write that was
 * refused went nowhere — no cache handler, no boundary, no toast. The three
 * generic write paths each failed silently in their own way, and the list
 * flashed SUCCESS over one.
 *
 * `MutationCache.onError` is the last resort, not the whole fix: the paths that
 * can do something specific (revert, keep the form dirty, decline to flash) do
 * it locally. This catches everything else — including the write doors #556 has
 * yet to flip, whose failures nobody has written a handler for because they do
 * not fail yet. `shouldReportWriteFailure` keeps it from talking over the
 * surfaces that already report properly.
 *
 * `retry: 0` on mutations is TanStack's default and is stated rather than
 * assumed: a refused write must not be re-sent, and neither must a half-applied
 * one that failed for any other reason.
 */
function createQueryClient() {
  return new QueryClient({
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (!shouldReportWriteFailure(mutation)) return
        reportWriteFailure(error, writeFailureSubject(mutation))
      },
    }),
    defaultOptions: {
      queries: { ...QUERY_FOCUS_RECOVERY_DEFAULTS, retry: isLive ? QUERY_RETRY : 0 },
      mutations: { retry: 0 },
    },
  })
}

export function SolumProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth()
  /* A query result is an answer under one caller's RLS, not public application
     state. Replace the entire cache synchronously when the principal changes so
     an account switch cannot render the previous actor's rows for even one
     frame. Token refreshes retain the same user id and therefore the same cache.
     Fixture mode has no authenticated principal and keeps one stable scope. */
  const actor = session?.user.id ?? (isLive ? 'signed-out' : 'fixture')
  const queryClient = useMemo(createQueryClient, [actor])
  return (
    <Ctx.Provider value={{ client, isLive }}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </Ctx.Provider>
  )
}
